import axios from 'axios';

/**
 * Dashboard Stats Service
 *
 * Wraps the `/dashboard/stats` endpoints documented in the backend
 * Stats Dashboard integration guide:
 *
 *   - Combined endpoint for first paint
 *   - Per-widget endpoints for granular refresh / polling
 *
 * All calls accept an optional AbortSignal so the caller (the store)
 * can cancel an in-flight request when filters change before the
 * response comes back. We never throw an "axios canceled" error to the
 * caller — those bubble up as `error.code === 'ERR_CANCELED'` and
 * should be ignored by the consumer.
 */
const buildParams = ({
  period,
  dateFrom,
  dateTo,
  scope,
  pipelineId,
  userId,
  installerUserId,
  limit,
} = {}) => {
  const params = {};
  if (period) params.period = period;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (scope) params.scope = scope;
  if (pipelineId) params.pipeline_id = pipelineId;
  if (userId) params.user_id = userId;
  if (installerUserId) params.installer_user_id = installerUserId;
  if (limit != null) params.limit = limit;
  return params;
};

export const dashboardStatsService = {
  /**
   * Combined endpoint — returns kpis, funnel, revenue_timeline,
   * pipeline, health, leaderboard, installer_stats in one round-trip.
   * Per-widget timeouts on the backend mean a single slow widget
   * surfaces as `{ error: 'timeout' | 'failed', section }` while the
   * rest of the payload remains usable.
   */
  async getCombined(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getKpis(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/kpis', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getFunnel(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/funnel', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getRevenueTimeline(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/revenue-timeline', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getPipeline(filters = {}, { signal } = {}) {
    // Pipeline has no period — only an optional pipeline_id filter.
    const response = await axios.get('/dashboard/stats/pipeline', {
      params: buildParams({ pipelineId: filters.pipelineId }),
      signal,
    });
    return response.data;
  },

  async getHealth(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/health', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getLeaderboard(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/leaderboard', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getRejectReasons(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/reject-reasons', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  async getInstallerStats(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/installer-stats', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  /**
   * Sold + invoiced revenue grouped per product and per category.
   * `limit` controls how many top products are returned (default 20
   * server-side); we always pass it explicitly so the frontend stays
   * in charge of how much it wants to render.
   */
  async getProducts(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/products', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  // ============================================================
  // Margin endpoints (added 2026-05-12)
  //
  // These read from `proposal_items.metadata.margin_snapshot` which
  // is only stamped on proposals created after the migration. Expect
  // low `coverage_pct` initially — the widgets show a tooltip when
  // coverage is below the configured threshold.
  //
  // None of these are part of the combined `/dashboard/stats` call;
  // the store fans them out as separate requests in parallel with
  // the combined fetch.
  // ============================================================

  /**
   * Headline brutomarge tile + trend vs. the directly preceding
   * period of the same length (current 30d → previous 30d).
   */
  async getMarginTotals(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/margin/totals', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  /**
   * Top-N best- and worst-performing products by realised margin.
   * The backend returns both lists in one response so the FE only
   * does one round-trip for the "best vs. worst" widget. `limit`
   * applies to each list independently (default 20, max 100).
   */
  async getMarginProducts(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/margin/products', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  /**
   * Red-flag list of individual proposal items where the realised
   * margin sits below the product's `min_margin_pct`. Sorted
   * worst-first by `gap_pct`. Lines with `min_margin_pct = 0` are
   * skipped server-side (no threshold = no alert).
   */
  async getMarginUnderThreshold(filters = {}, { signal } = {}) {
    const response = await axios.get('/dashboard/stats/margin/under-threshold', {
      params: buildParams(filters),
      signal,
    });
    return response.data;
  },

  // ============================================================
  // Forecast endpoint (added Phase 3 — 2026-05-12; window-mode
  // delta 2026-05-13)
  //
  // "Prognose vs realisatie" widget. Reads the per-org revenue target
  // and projects EOY using both a season-aware curve and a naive
  // linear extrapolation. Source of truth for "sold" is
  // `proposals.accepted_at`.
  //
  // Two modes:
  //   - **Year-mode** — pass `year`. Response carries `months[]`
  //     + `ytd_*` summary + EOY projections.
  //   - **Window-mode** — pass `period` (and `date_from`/`date_to`
  //     for `period === 'custom'`). Response carries
  //     `data.mode === 'window'`, `buckets[]`, `interval`, and a
  //     `window_*` summary (no EOY projections — they don't make
  //     sense for an arbitrary window).
  //
  // When no target is set for the covered year(s) the backend still
  // responds 200 with `target_set: false` and zeroed targets, so the
  // widget can render an empty-state without an extra round-trip.
  // ============================================================

  /**
   * Forecast — year-mode or window-mode depending on the args. Pass
   * `year` for year-mode; pass `period` (+ optional dates) for
   * window-mode. The two are mutually exclusive on the server, so
   * we send only one of them.
   *
   * @param {object} params
   * @param {number} [params.year] — year-mode driver
   * @param {string} [params.period] — window-mode driver (7d|30d|90d|mtd|qtd|ytd|all|custom)
   * @param {string} [params.dateFrom] — required when `period === 'custom'`
   * @param {string} [params.dateTo]   — required when `period === 'custom'`
   */
  async getForecast({ year, period, dateFrom, dateTo } = {}, { signal } = {}) {
    const params = {};
    if (period) {
      // Window-mode wins when both are provided — `period` is the
      // explicit "follow dashboard filter" intent, `year` would be
      // a stale leftover from the previous mode.
      params.period = period;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else if (year != null) {
      params.year = year;
    }
    const response = await axios.get('/dashboard/stats/forecast', {
      params,
      signal,
    });
    return response.data;
  },

  /**
   * Planned / unplanned / realized installation revenue for a window.
   * Planning uses period=custom with the visible day or week.
   */
  async getPlanned(filters = {}, { signal } = {}) {
    const params = {
      ...buildParams(filters),
      include_items: filters.includeItems === false ? 'false' : 'true',
      include_unplanned: filters.includeUnplanned === false ? 'false' : 'true',
    };
    if (filters.includeDisplay === false) params.include_display = 'false';
    const response = await axios.get('/dashboard/stats/planned', {
      params,
      signal,
    });
    return response.data;
  },
};

export default dashboardStatsService;
